/**
 * Dashboard Utility Functions
 * Centralized logic for deal calculations and filtering
 */

// Stage probability weights for revenue forecasting
export const STAGE_PROBABILITY = {
  prospecting: 0.10,
  qualification: 0.20,
  proposal: 0.50,
  negotiation: 0.75,
  closed_won: 1.00,
};

/**
 * Parse deal amount from string or number format
 */
export const getDealAmount = (deal) => {
  if (typeof deal.amount === "string") {
    return Number(deal.amount.replace(/[$,]/g, "")) || 0;
  }
  if (typeof deal.amount === "number") {
    return deal.amount;
  }
  return 0;
};

/**
 * Get week range for a given offset (0 = this week, -1 = last week, etc.)
 */
export const getWeekRange = (offset = 0) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(startOfWeek.getDate() + offset * 7);
  
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return { start: startOfWeek, end: endOfWeek };
};

/**
 * Get month range for a given offset (0 = this month, -1 = last month, etc.)
 */
export const getMonthRange = (offset = 0) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
};

/**
 * Filter deals by period (all, weekly, monthly)
 */
export const filterDealsByPeriod = (deals, period = "all", offset = 0) => {
  if (period === "all") return deals;
  
  if (period === "weekly") {
    const { start, end } = getWeekRange(offset);
    return deals.filter((deal) => {
      const d = new Date(deal.created_at);
      return d >= start && d <= end;
    });
  }
  
  // monthly
  const { start, end } = getMonthRange(offset);
  return deals.filter((deal) => {
    const d = new Date(deal.created_at);
    return d >= start && d <= end;
  });
};

/**
 * Calculate weighted revenue for a set of deals
 * @param {Array} deals - Array of deal objects
 * @param {Object} stageProbability - Stage to probability mapping
 * @param {Array} excludeStages - Stages to exclude from calculation
 * @returns {number} Total weighted revenue
 */
export const calculateWeightedRevenue = (
  deals,
  stageProbability = STAGE_PROBABILITY,
  excludeStages = []
) => {
  return deals.reduce((sum, deal) => {
    const stageKey = (deal.stage || "").toLowerCase();
    
    if (excludeStages.includes(stageKey)) {
      return sum;
    }
    
    const probability = stageProbability[stageKey];
    if (probability == null) return sum;
    
    return sum + getDealAmount(deal) * probability;
  }, 0);
};

/**
 * Build pipeline data from stages and deals
 * @param {Array} stages - Stage configuration array
 * @param {Array} deals - Deal data array
 * @param {Object} stageProbability - Stage to probability mapping
 * @returns {Array} Pipeline data with aggregates per stage
 */
export const buildPipelineData = (
  stages = [],
  deals = [],
  stageProbability = STAGE_PROBABILITY
) => {
  const pipelineData = (stages || []).map((s) => ({
    stage: s.label,
    key: s.key,
    deals: 0,
    amount: 0,
    projectedRevenue: 0,
    probability: Math.round((stageProbability[s.key] ?? 0) * 100),
    color: s.color || "#94a3b8",
  }));

  deals.forEach((deal) => {
    const key = (deal.stage || "").toLowerCase();
    const idx = pipelineData.findIndex((p) => p.key === key);
    
    if (idx >= 0) {
      pipelineData[idx].deals += 1;
      const amount = getDealAmount(deal);
      pipelineData[idx].amount += amount;
      pipelineData[idx].projectedRevenue += amount * (stageProbability[key] ?? 0);
    }
  });

  return pipelineData;
};

/**
 * Calculate qualified pipeline from pipeline data
 * Excludes closed_won and closed_lost stages
 */
export const calculateQualifiedPipeline = (pipelineData) => {
  return pipelineData.reduce((sum, stage) => {
    const key = (stage.key || "").toLowerCase();
    if (key === "closed_won" || key === "closed_lost") return sum;
    return sum + (stage.projectedRevenue || 0);
  }, 0);
};

/**
 * Calculate total unweighted pipeline value
 * Excludes closed_lost by default
 */
export const calculateTotalPipelineValue = (
  deals,
  excludeStages = ["closed_lost"]
) => {
  return deals.reduce((sum, deal) => {
    const stageKey = (deal.stage || "").toLowerCase();
    if (excludeStages.includes(stageKey)) return sum;
    return sum + getDealAmount(deal);
  }, 0);
};
